import { Box, AlertBox, AlertBoxDismissButton } from '@vp/swan';
import React, { useRef, useState, useContext, useEffect, useCallback } from 'react';
import { CAContext } from './src-contexts-CAContext.js';
import { MiawChatContext } from './src-contexts-MiawChatContext.js';
import ChatFooter from './src-components-ChatWindow-ChatFooter-ChatFooter.js';
import ChatHeader from './src-components-ChatWindow-ChatHeader-ChatHeader.js';
import ChatWindowContent from './src-components-ChatWindow-ChatWindowContent-ChatWindowContent.js';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import './src-components-ChatWindow-ChatWindow.styles.scss.js';
import ErrorMessage from './src-components-ErrorMessage-ErrorMessage.js';
import EndChatPage from './src-components-EndChatPage-EndChatPage.js';
import PrechatFormMIAW from './src-components-PrechatFormMIAW-PrechatFormMIAW.js';
import { useIdentity } from '@vp/auth-react';
import { useCustomChatEvents } from './src-hooks-custom-hooks-useCustomChatEvents.js';
import { clearChatWindowState, clearChatbotToken, clearChatSessionType } from './src-utils-session-storage-utils.js';
import { sendEventToHelpAnywherePanel } from './src-utils-chat-help-panel-utils.js';
import { isStudioPage } from './src-utils-chat-page-utils.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { useKeyboardAwareFooter } from './src-components-ChatWindow-ChatFooter-useFooterRef.js';
import EndChatFeedback from './src-components-EndChatPage-EndChatFeedback.js';

const ChatWindow = () => {
  const footerRef = useRef(null);
  const isKeyboardOpen = useKeyboardAwareFooter(footerRef);
  const { logCustomAttributesNR } = useNRLogging();
  const identity = useIdentity();
  const [showChatWindow, setShowChatWindow] = useState(false);
  const [showEndChatPage, setShowEndChatPage] = useState(false);
  const { sendChatEvents } = useCustomChatEvents();
  const {
    chatState: { chatWindow, enableChatbot, chatSession, userInfo, chatButtonHandler, scrollToBottom },
    setChatState
  } = useContext(CAContext);
  const {
    state: { conversationMessages: agentMessages, startMiawChatSession, sendMiawChatMessage, attachmentErrorMessage, showAttachmentError, isMIAWApiError },
    setState: setMIAWContext
  } = useContext(MiawChatContext);
  const {
    state: { conversationMessages: chatbotMessages, sendChatbotMessage, showErrorMessage, isSessionExpired },
    setState: setChatbotContext
  } = useContext(ChatbotContext);
  const [conversationMessages, setConversationMessages] = useState();
  const startAgentChat = (data) => {
    if (!userInfo?.isLoggedIn) {
      setChatState((prevState) => ({
        ...prevState,
        userInfo: {
          isLoggedIn: false,
          userData: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            shopperId: identity.canonicalId || ""
          }
        }
      }));
    }
    startMiawChatSession?.(data);
  };
  useEffect(() => {
    setConversationMessages([...chatbotMessages, ...agentMessages]);
  }, [agentMessages, chatbotMessages]);
  const onCloseChat = useCallback(() => {
    logCustomAttributesNR("ca-chatbot-info", {
      logName: "close_button_clicked",
      logLevel: "info",
      message: "User clicked on close button from chat window"
    });
    if (chatSession === "miawChatSession" || chatSession === "chatbotSession") {
      setShowEndChatPage(true);
    } else if (chatSession === "initial" || chatSession === "endChatSession") {
      if (isStudioPage()) {
        sendEventToHelpAnywherePanel("showHelpPanelButton");
        chatWindowHandler("hide");
        chatButtonHandler("hide");
      } else {
        sendChatEvents("onChatReady", {});
        onMinimize();
      }
      clearChatWindowState();
    } else {
      onMinimize();
    }
  }, [chatSession]);
  const onCancelEndChat = () => {
    setShowEndChatPage(false);
  };
  const onEndChatFlowComplete = () => {
    setShowEndChatPage(false);
    setChatState((prevState) => ({
      ...prevState,
      chatSession: "initial"
    }));
  };
  const onSessionExpiredStartNewChat = () => {
    setChatState((prevState) => ({
      ...prevState,
      chatSession: "initial",
      endChatInfo: void 0,
      isPrivacyPolicyAccepted: false
    }));
    setChatbotContext((prev) => ({
      ...prev,
      isSessionExpired: false,
      conversationMessages: [],
      showErrorMessage: false,
      isChatbotEndError: false,
      customFlow: void 0
    }));
    setMIAWContext((prev) => ({
      ...prev,
      conversationMessages: [],
      showErrorMessage: false,
      isMIAWApiError: false,
      conversationId: void 0,
      showAttachmentError: false
    }));
    clearChatbotToken();
    clearChatSessionType();
  };
  useEffect(() => {
    if (showEndChatPage) {
      setMIAWContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true }))
      }));
      setChatbotContext((prevState) => ({
        ...prevState,
        conversationMessages: prevState.conversationMessages.map((message) => ({ ...message, isMessageRead: true }))
      }));
    }
  }, [showEndChatPage]);
  const onMinimize = () => {
    setChatState((prevState) => ({
      ...prevState,
      chatWindow: "minimized"
    }));
    sendChatEvents("onMinimize", {});
    logCustomAttributesNR("ca-chatbot-info", {
      logName: "minimize_button_clicked",
      logLevel: "info",
      message: "User clicked on minimize button from chat window"
    });
  };
  const handleMessage = (data) => {
    if (chatSession === "miawChatSession") {
      sendMiawChatMessage?.(data);
    } else {
      sendChatbotMessage?.({ type: "text_message", messageData: data });
    }
  };
  useEffect(() => {
    const chatWindow2 = document.getElementById("chatWindowContainer");
    if (chatWindow2) {
      if (isKeyboardOpen) {
        chatWindow2.classList.add("chatWindowMobileKeyboard");
        scrollToBottom?.();
      } else {
        chatWindow2.classList.remove("chatWindowMobileKeyboard");
        scrollToBottom?.();
      }
    }
  }, [isKeyboardOpen]);
  useEffect(() => {
    if (chatWindow === "minimized") {
      setShowChatWindow(false);
    } else if (chatWindow === "maximized") {
      setShowChatWindow(true);
      if (isStudioPage()) {
        sendEventToHelpAnywherePanel("hideChatLoader");
      }
    }
  }, [chatWindow]);
  const chatWindowHandler = useCallback((action) => {
    if (action === "show") {
      setShowChatWindow(true);
    } else if (action === "hide") {
      setShowChatWindow(false);
    }
  }, []);
  useEffect(() => {
    setChatState((prevState) => ({
      ...prevState,
      chatWindowHandler
    }));
  }, [chatWindowHandler]);
  if (chatSession === "initial" && !enableChatbot) {
    return /* @__PURE__ */ React.createElement(Box, { className: "chatWindowContainer", compactMode: true, display: showChatWindow ? "flex" : "none" }, /* @__PURE__ */ React.createElement(ChatHeader, { onChatClose: onCloseChat, onChatMinimize: onMinimize }), /* @__PURE__ */ React.createElement(PrechatFormMIAW, { onSubmit: startAgentChat }));
  }
  if (showEndChatPage && chatSession !== "initial") {
    return /* @__PURE__ */ React.createElement(Box, { className: "chatWindowContainer", compactMode: true, display: showChatWindow ? "flex" : "none" }, /* @__PURE__ */ React.createElement(ChatHeader, { onChatClose: onCloseChat, onChatMinimize: onMinimize }), /* @__PURE__ */ React.createElement(EndChatPage, { onCancelEnd: onCancelEndChat, onEndChatFlowComplete }), /* @__PURE__ */ React.createElement(ChatFooter, { onSendMessage: handleMessage, disableInput: true }));
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { className: `chatWindowContainer`, compactMode: true, display: showChatWindow ? "flex" : "none", id: "chatWindowContainer" }, /* @__PURE__ */ React.createElement(ChatHeader, { onChatClose: onCloseChat, onChatMinimize: onMinimize }), isSessionExpired ? /* @__PURE__ */ React.createElement(EndChatFeedback, { isSessionExpired, onStartNewChat: onSessionExpiredStartNewChat }) : /* @__PURE__ */ React.createElement(React.Fragment, null, (showErrorMessage || isMIAWApiError) && /* @__PURE__ */ React.createElement(ErrorMessage, null), showAttachmentError && /* @__PURE__ */ React.createElement(Box, { className: "AlertBoxContainer" }, /* @__PURE__ */ React.createElement(AlertBox, { skin: "error" }, attachmentErrorMessage, /* @__PURE__ */ React.createElement(
    AlertBoxDismissButton,
    {
      accessibleText: "Dismiss alert",
      onClick: () => setMIAWContext((prev) => ({ ...prev, attachmentErrorMessage: "", showAttachmentError: false }))
    }
  ))), /* @__PURE__ */ React.createElement(ChatWindowContent, { conversationMessages: conversationMessages || [] })), /* @__PURE__ */ React.createElement(ChatFooter, { onSendMessage: handleMessage, ref: footerRef, isKeyboardVisible: isKeyboardOpen })));
};

export { ChatWindow as default };
//# sourceMappingURL=src-components-ChatWindow-ChatWindow.js.map
